import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@app/auth/auth.service';
import { ToastService } from '@teorema/patron';
import { Subscription } from 'rxjs';
import { AccessType, System } from 'src/app/models';
import { utils } from 'src/app/shared';

@Component({
  template: '@if (loading) {<span>Redirecionando...</span>}',
  standalone: false,
})
export class AutoLoginComponent implements OnInit, OnDestroy {
  sub?: Subscription;
  loading: boolean = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.loading = true;
    this.sub = this.activatedRoute.queryParams.subscribe(async params => {
      const token = params?.['token'];
      const companyGroupId = params?.['company_group_id'];
      const customerId = params?.['customer_id'];
      const redirect = params?.['redirect'];
      const resetPassword = params?.['reset_password'];

      if (token) {
        utils.clearStorageInfo();
        utils.setToken(token);
        this.authService.changePasswordModalShown = false;
        this.authService.forcePasswordReset = !!resetPassword && resetPassword != '0';
        const system = utils.getSystem();
        if (system == System.CUSTOMERS) {
          utils.setAccessType(AccessType.Customer);
        } else if (system == System.QUOTES) {
          utils.setAccessType(AccessType.Supplier);
        } else {
          // Nos painéis de usuário o login normal define o access-type a partir do
          // acesso selecionado; sem ele o header access-type não é enviado e as
          // validações de permissão/sessão do backend falham.
          utils.setAccessType(AccessType.User);
        }

        // O token do link não possui sessão de licença; sem trocá-lo as rotas que
        // validam a sessão (ex.: set-config) respondem 401 e derrubam o acesso.
        const session = await this.authService.createSessionToken(companyGroupId).catch(() => null);
        if (session?.token) utils.setToken(session.token);

        const user = await this.authService.me(companyGroupId).catch(error => {
          this.toastService.show({
            color: 'error',
            message: 'Houve um erro ao efetuar a autenticação',
          });
        });

        if (!user) {
          this.authService.logout();
          return;
        }

        let companyGroup: any = null;
        if (user.company_groups) {
          companyGroup = user.company_groups.find((cg: any) => {
            if (cg.pivot?.customer_id && customerId)
              return cg.pivot.customer_id === customerId && cg.id === companyGroupId;
            return cg.id === companyGroupId;
          });
          if (companyGroup) utils.setCompanyGroup(companyGroup);
        }

        user.company_groups = [];
        utils.setUser(user);

        if (system == System.CUSTOMERS) {
          if (companyGroup)
            utils.setAvailableAccesses([
              { ...companyGroup, access_type: { key: AccessType.Customer, label: 'Cliente' } },
            ]);
        } else if (system == System.QUOTES) {
          utils.setAvailableAccesses([
            { access_type: { key: AccessType.Supplier, label: 'Fornecedor' } },
          ]);
        } else if (companyGroup) {
          utils.setAvailableAccesses([
            { ...companyGroup, access_type: { key: AccessType.User, label: 'Usuário' } },
          ]);
        }

        let redirectPath = this.authService.getCoreConfig().redirectPathAfterLogin;
        if (this.authService.checkSystem([System.QUOTES]) && utils.isSupplier()) {
          redirectPath = 'cotacoes-fornecedor';
        }
        let url = redirectPath ?? '/';
        if (redirect) url += `/${redirect}`;

        this.router.navigate([url]);
      }

      this.loading = false;
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }
}
